import { useMemo, useState } from "react";
import {
  INTEGRATED_JOB_AIDS,
  LEARNING_FORMAT_ROADMAP,
} from "../lib/forfeoIntegratedJobAids";
import {
  LEARNING_FORMATS,
  LEARNING_PROFILE_CATALOG,
  type LearningProfile,
} from "../lib/forfeoIntegratedLearningContract";

type Locale = "fr" | "en";
type ProfileFilter = "all" | LearningProfile;

const copy = {
  fr: {
    eyebrow: "Centre d’aide Forfeo",
    title: "Trouvez votre parcours, puis avancez étape par étape.",
    intro: "Sept parcours bilingues réunissent guides PDF, présentations et démonstrations interactives accessibles. Aucun contenu n’utilise de donnée de production.",
    locale: "Langue",
    profiles: "Choisir un profil",
    all: "Tous les profils",
    search: "Rechercher dans les guides",
    placeholder: "Ex. commande, bail, soutien…",
    results: "guides disponibles",
    steps: "Étapes du guide",
    assets: "Ressources de formation",
    downloadPdf: "Télécharger le guide PDF",
    downloadPresentation: "Télécharger la présentation",
    open3d: "Ouvrir la démonstration 3D",
    noResult: "Aucun guide ne correspond à ces filtres.",
    reset: "Réinitialiser les filtres",
    roadmap: "Formats de formation",
    roadmapIntro: "Les cinq formats prévus sont disponibles avec I3. Chaque démonstration 3D conserve une alternative textuelle accessible.",
    available: "Disponible",
    fallback: "Alternative accessible incluse",
    boundaryTitle: "Une aide sûre par conception",
    boundary: "Giftshop demeure l’autorité financière. Ces ressources expliquent les parcours sans modifier de paiement, de remboursement ou de donnée de production.",
  },
  en: {
    eyebrow: "Forfeo Help Centre",
    title: "Find your journey, then move forward step by step.",
    intro: "Seven bilingual journeys combine PDF job aids, presentations and accessible interactive demonstrations. No content uses production data.",
    locale: "Language",
    profiles: "Choose a profile",
    all: "All profiles",
    search: "Search the guides",
    placeholder: "E.g. order, lease, support…",
    results: "guides available",
    steps: "Guide steps",
    assets: "Training resources",
    downloadPdf: "Download the PDF job aid",
    downloadPresentation: "Download the presentation",
    open3d: "Open the 3D demonstration",
    noResult: "No guide matches these filters.",
    reset: "Reset filters",
    roadmap: "Training formats",
    roadmapIntro: "All five planned formats are available with I3. Every 3D demonstration includes an accessible text alternative.",
    available: "Available",
    fallback: "Accessible alternative included",
    boundaryTitle: "Help designed to be safe",
    boundary: "Giftshop remains the financial authority. These resources explain journeys without changing payments, refunds or production data.",
  },
} as const;

function initialProfile(): ProfileFilter {
  const profile = new URLSearchParams(window.location.search).get("profil");
  return profile && profile in LEARNING_PROFILE_CATALOG ? profile as LearningProfile : "all";
}

const assetClass = "inline-flex min-h-11 items-center rounded-full border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-black text-cyan-950 transition hover:border-cyan-500 hover:bg-cyan-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700";

export default function IntegratedHelpCenter() {
  const [locale, setLocale] = useState<Locale>("fr");
  const [profile, setProfile] = useState<ProfileFilter>(initialProfile);
  const [query, setQuery] = useState("");
  const text = copy[locale];

  const guides = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase(locale);
    return INTEGRATED_JOB_AIDS.filter((guide) => {
      if (profile !== "all" && guide.profile !== profile) return false;
      if (!normalizedQuery) return true;
      const searchable = [
        guide.title[locale],
        guide.summary[locale],
        ...guide.steps.flatMap((currentStep) => [currentStep.title[locale], currentStep.detail[locale]]),
      ].join(" ").toLocaleLowerCase(locale);
      return searchable.includes(normalizedQuery);
    });
  }, [locale, profile, query]);

  const resetFilters = () => {
    setProfile("all");
    setQuery("");
  };

  return (
    <main
      data-forfeo-integrated-help-center="active"
      data-forfeo-training-assets="i3-complete"
      className="min-h-screen bg-slate-50 px-4 pb-20 pt-24 text-slate-950 sm:px-6 lg:px-8"
    >
      <div className="mx-auto w-full max-w-7xl">
        <header className="overflow-hidden rounded-[2rem] bg-slate-950 px-5 py-8 text-white shadow-xl sm:px-8 sm:py-10 lg:px-12">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-cyan-300">{text.eyebrow}</p>
              <h1 className="mt-3 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">{text.title}</h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-200 sm:text-lg">{text.intro}</p>
            </div>
            <fieldset aria-label={text.locale} className="shrink-0">
              <legend className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-300">{text.locale}</legend>
              <div className="inline-flex rounded-full bg-white/10 p-1">
                {(["fr", "en"] as const).map((currentLocale) => (
                  <button
                    key={currentLocale}
                    type="button"
                    aria-pressed={locale === currentLocale}
                    onClick={() => setLocale(currentLocale)}
                    className={`min-h-11 rounded-full px-5 text-sm font-black transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300 ${locale === currentLocale ? "bg-white text-slate-950" : "text-white hover:bg-white/10"}`}
                  >
                    {currentLocale.toUpperCase()}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>
        </header>

        <section aria-labelledby="profile-filter-title" className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          <h2 id="profile-filter-title" className="text-xl font-black">{text.profiles}</h2>
          <div className="mt-4 flex gap-2 overflow-x-auto pb-2" role="group" aria-label={text.profiles}>
            <button
              type="button"
              aria-pressed={profile === "all"}
              onClick={() => setProfile("all")}
              className={`min-h-11 shrink-0 rounded-full border px-4 text-sm font-bold ${profile === "all" ? "border-slate-950 bg-slate-950 text-white" : "border-slate-300 bg-white hover:bg-slate-50"}`}
            >
              {text.all}
            </button>
            {(Object.keys(LEARNING_PROFILE_CATALOG) as LearningProfile[]).map((profileKey) => (
              <button
                key={profileKey}
                type="button"
                aria-pressed={profile === profileKey}
                onClick={() => setProfile(profileKey)}
                className={`min-h-11 shrink-0 rounded-full border px-4 text-sm font-bold ${profile === profileKey ? "border-cyan-700 bg-cyan-700 text-white" : "border-slate-300 bg-white hover:bg-slate-50"}`}
              >
                {LEARNING_PROFILE_CATALOG[profileKey].label[locale]}
              </button>
            ))}
          </div>

          <label className="mt-5 block max-w-2xl text-sm font-bold" htmlFor="forfeo-help-search">{text.search}</label>
          <input
            id="forfeo-help-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={text.placeholder}
            className="mt-2 min-h-12 w-full max-w-2xl rounded-2xl border border-slate-300 bg-white px-4 text-base outline-none ring-cyan-600 transition focus:border-cyan-600 focus:ring-2"
          />
        </section>

        <div className="mt-7 flex items-center justify-between gap-4">
          <p className="text-sm font-bold text-slate-600" aria-live="polite">
            <span className="text-xl font-black text-slate-950">{guides.length}</span> {text.results}
          </p>
          {(profile !== "all" || query) && (
            <button type="button" onClick={resetFilters} className="min-h-11 rounded-full px-4 text-sm font-bold text-cyan-800 underline-offset-4 hover:underline">{text.reset}</button>
          )}
        </div>

        {guides.length > 0 ? (
          <section aria-label={text.results} className="mt-4 grid gap-5 lg:grid-cols-2">
            {guides.map((guide) => (
              <article key={guide.slug} data-forfeo-help-profile={guide.profile} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <p className="inline-flex rounded-full bg-cyan-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-cyan-900">{LEARNING_PROFILE_CATALOG[guide.profile].label[locale]}</p>
                <h2 className="mt-4 text-2xl font-black">{guide.title[locale]}</h2>
                <p className="mt-3 leading-7 text-slate-600">{guide.summary[locale]}</p>
                <h3 className="mt-6 text-sm font-black uppercase tracking-widest text-slate-500">{text.steps}</h3>
                <ol className="mt-4 space-y-4">
                  {guide.steps.map((currentStep, index) => (
                    <li key={currentStep.id} data-forfeo-help-step={currentStep.id} className="flex gap-4">
                      <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-950 text-sm font-black text-white">{index + 1}</span>
                      <div>
                        <p className="font-black">{currentStep.title[locale]}</p>
                        <p className="mt-1 text-sm leading-6 text-slate-600">{currentStep.detail[locale]}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <h3 className="mt-7 text-sm font-black uppercase tracking-widest text-slate-500">{text.assets}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a data-forfeo-training-asset="pdf" href={guide.assets.pdf} download className={assetClass}>{text.downloadPdf}</a>
                  <a data-forfeo-training-asset="presentation" href={guide.assets.presentation} download className={assetClass}>{text.downloadPresentation}</a>
                  <a data-forfeo-training-asset="immersive-3d" href={guide.assets.immersiveDemo} className={assetClass}>{text.open3d}</a>
                </div>
              </article>
            ))}
          </section>
        ) : (
          <section className="mt-4 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="font-bold text-slate-600">{text.noResult}</p>
            <button type="button" onClick={resetFilters} className="mt-4 min-h-11 rounded-full bg-slate-950 px-5 text-sm font-black text-white">{text.reset}</button>
          </section>
        )}

        <section aria-labelledby="training-formats-title" className="mt-10 rounded-3xl bg-cyan-950 p-5 text-white sm:p-8">
          <h2 id="training-formats-title" className="text-2xl font-black sm:text-3xl">{text.roadmap}</h2>
          <p className="mt-3 max-w-3xl leading-7 text-cyan-100">{text.roadmapIntro}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {LEARNING_FORMAT_ROADMAP.map((item) => (
              <article key={item.format} data-forfeo-help-format={item.format} className="rounded-2xl bg-white/10 p-4">
                <p className="font-black">{LEARNING_FORMATS[item.format].label[locale]}</p>
                <p className="mt-2 text-sm font-bold text-cyan-200">{text.available}</p>
                {item.accessibleFallbackRequired && <p className="mt-2 text-xs leading-5 text-cyan-100">{text.fallback}</p>}
              </article>
            ))}
          </div>
        </section>

        <aside className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50 p-5 sm:p-7">
          <h2 className="text-xl font-black text-emerald-950">{text.boundaryTitle}</h2>
          <p className="mt-2 max-w-4xl leading-7 text-emerald-900">{text.boundary}</p>
        </aside>
      </div>
    </main>
  );
}
